import {formatCents,type Cents} from "../domain/money";

/**
 * Document import arithmetic (Phase 9D, spec §33A) — PURE and
 * deterministic. AI may read the numbers; only this code decides whether
 * they add up, and it never bends them to fit.
 */

export interface DocumentTotalsInput {
  /** Sum of the included line amounts (merchandise, pre-tax). */
  lineTotalCents: Cents;
  subtotalCents: Cents | null;
  discountCents: Cents | null;
  taxCents: Cents | null;
  shippingCents: Cents | null;
  totalCents: Cents | null;
}

export interface ReconciliationCheck {
  ok: boolean;
  /** Human sentences explaining exactly what disagrees. */
  problems: string[];
  /** subtotal − discount + tax + shipping, when computable. */
  computedTotalCents: Cents | null;
  /** computed − stated total; null when either is unknown. */
  totalDifferenceCents: Cents | null;
  /** lines − subtotal; null when the subtotal is unknown. */
  subtotalDifferenceCents: Cents | null;
}

/**
 * Validate the extraction against itself. Two independent checks:
 *   Σ lines  ==  subtotal
 *   subtotal − discount + tax + shipping  ==  total
 * A document that fails either is flagged for review — never silently
 * adjusted, and never imported as if it balanced.
 */
export function checkDocumentArithmetic(input: DocumentTotalsInput): ReconciliationCheck {
  const problems: string[] = [];
  const subtotal = input.subtotalCents;
  const discount = input.discountCents ?? 0;
  const tax = input.taxCents ?? 0;
  const shipping = input.shippingCents ?? 0;

  let subtotalDifference: Cents | null = null;
  if (subtotal !== null) {
    subtotalDifference = input.lineTotalCents - subtotal;
    if (subtotalDifference !== 0) {
      problems.push(
        `The line items add up to ${fmt(input.lineTotalCents)} but the document's subtotal is ${fmt(subtotal)} (${signed(subtotalDifference)}).`,
      );
    }
  }

  let computedTotal: Cents | null = null;
  let totalDifference: Cents | null = null;
  const base = subtotal ?? (input.lineTotalCents || null);
  if (base !== null) {
    computedTotal = base - discount + tax + shipping;
    if (input.totalCents !== null) {
      totalDifference = computedTotal - input.totalCents;
      if (totalDifference !== 0) {
        problems.push(
          `Subtotal − discount + tax + shipping comes to ${fmt(computedTotal)}, but the document total is ${fmt(input.totalCents)} (${signed(totalDifference)}).`,
        );
      }
    }
  }

  if (subtotal === null && input.totalCents === null) {
    problems.push("Neither a subtotal nor a total was read from the document.");
  }

  return {
    ok: problems.length === 0,
    problems,
    computedTotalCents: computedTotal,
    totalDifferenceCents: totalDifference,
    subtotalDifferenceCents: subtotalDifference,
  };
}

/** Confirmation gate: no tolerance, residual line, or sign conversion of mixed receipts. */
export function receiptImportTotals(amounts: number[], input: Omit<DocumentTotalsInput,"lineTotalCents">, isReturn: boolean) {
  const values=[...amounts,input.subtotalCents,input.discountCents,input.taxCents,input.shippingCents,input.totalCents].filter((value):value is number=>value!==null);
  if(values.some(value=>!Number.isSafeInteger(value)))throw Error("Every amount must be whole, safe integer cents.");
  if(input.totalCents===null)throw Error("Enter and verify the document total before importing.");
  if(amounts.some(value=>value<0)&&amounts.some(value=>value>0))throw Error("Mixed sale and return lines need separate purchase and linked return records; no negative line will be converted to a positive cost.");
  if(!isReturn&&amounts.some(value=>value<0))throw Error("Negative lines require an explicit return document and original purchase.");
  if(!isReturn&&[input.subtotalCents,input.discountCents,input.taxCents,input.shippingCents,input.totalCents].some(value=>value!==null&&value<0))throw Error("Negative receipt totals need explicit return review; correct the sign treatment before importing.");
  const merchandise=amounts.reduce((sum,value)=>sum+Math.abs(value),0);
  const normalized={lineTotalCents:merchandise,subtotalCents:input.subtotalCents===null?null:Math.abs(input.subtotalCents),discountCents:Math.abs(input.discountCents??0),taxCents:Math.abs(input.taxCents??0),shippingCents:Math.abs(input.shippingCents??0),totalCents:Math.abs(input.totalCents)};
  const check=checkDocumentArithmetic(normalized);
  if(!Number.isSafeInteger(merchandise)||!Number.isSafeInteger(merchandise-normalized.discountCents+normalized.taxCents+normalized.shippingCents))throw Error("The document amounts exceed the supported safe-cent range.");
  if(!check.ok)throw Error(`Reconcile the document before importing. ${check.problems.join(" ")}`);
  if(merchandise-normalized.discountCents+normalized.taxCents+normalized.shippingCents!==normalized.totalCents)throw Error("Reconcile the document total to the entered lines, discount, tax and shipping before importing.");
  return {merchandise,discount:normalized.discountCents,tax:normalized.taxCents,shipping:normalized.shippingCents,total:normalized.totalCents};
}

const fmt = (c: Cents): string => formatCents(c);
const signed = (c: Cents): string => `${c > 0 ? "+" : ""}${fmt(c)}`;

/* ------------------------------------------------------------------ */
/* Purchased-quantity analysis (spec §33A / §29C)                      */
/* ------------------------------------------------------------------ */

export interface QuantityAnalysisInput {
  /** Base construction quantity BEFORE waste (×100), e.g. floor area. */
  baseQuantityX100: number;
  /** Assembly waste allowance (×100 of the base), e.g. 10% → 1000. */
  wastePctX100: number;
  /** Construction requirement including waste (×100). */
  requiredX100: number;
  /** Coverage of one purchase unit (×100), e.g. 23.66 sqft/case → 2366. */
  coveragePerUnitX100: number;
  /** Whole purchase units the plan says to buy (BOM, package-rounded). */
  plannedPurchaseUnits: number;
  /** Whole purchase units actually bought, from confirmed receipts (×100). */
  purchasedUnitsX100: number;
  unit: string;
  purchaseUnit: string;
}

export interface QuantityAnalysis {
  baseQuantityX100: number;
  wasteQuantityX100: number;
  requiredX100: number;
  /** Coverage the PLAN's package rounding buys beyond the requirement. */
  packageRoundingExcessX100: number;
  purchasedUnitsX100: number;
  /** Coverage actually purchased (×100). */
  purchasedCoverageX100: number;
  /** Purchased coverage − required. */
  excessBeyondPlanX100: number;
  excessPctX100: number | null;
  unit: string;
  purchaseUnit: string;
}

/**
 * Compare what was actually bought against what the plan required, keeping
 * the four quantities DISTINCT (spec §33A): base construction quantity,
 * construction waste, package-rounding excess, and actual purchased
 * excess. Collapsing them hides which decision caused the overage.
 */
export function analyzePurchasedQuantity(input: QuantityAnalysisInput): QuantityAnalysis {
  const waste = input.requiredX100 - input.baseQuantityX100;
  const plannedCoverage = input.plannedPurchaseUnits * input.coveragePerUnitX100;
  const purchasedCoverage = Math.round((input.purchasedUnitsX100 * input.coveragePerUnitX100) / 100);
  const excess = purchasedCoverage - input.requiredX100;
  return {
    baseQuantityX100: input.baseQuantityX100,
    wasteQuantityX100: waste,
    requiredX100: input.requiredX100,
    packageRoundingExcessX100: plannedCoverage - input.requiredX100,
    purchasedUnitsX100: input.purchasedUnitsX100,
    purchasedCoverageX100: purchasedCoverage,
    excessBeyondPlanX100: excess,
    excessPctX100:
      input.requiredX100 === 0 ? null : Math.round((excess * 10000) / input.requiredX100),
    unit: input.unit,
    purchaseUnit: input.purchaseUnit,
  };
}
